import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { nodeURL } from "../../configUrl";
import { toast } from "react-toastify";
import { openCariPdf } from "../../utils/cariPdf";
import { groupBorcRows } from "../../utils/cariGrouping";

const generateIdempotencyKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const formatTutar = (value) => `${Number(value || 0).toFixed(2)} ₺`;
const formatTarih = (value) => (value ? new Date(value).toLocaleDateString("tr-TR") : "-");

// firmalar.js ("Cari Gör") ve kiralama düzenleme ekranı ("Firma Ödemesi / Cari")
// arasında paylaşılan tek cari (borç/ödeme) penceresi. Daha önce bu iki yerde
// birbirinden bağımsız, birbirinden eksik iki kopya olarak yazılıydı.
const FirmaCariModal = ({ show, onClose, firmaId, firmaAdi }) => {
  const [cari, setCari] = useState(null);
  const [showCariDetails, setShowCariDetails] = useState(false);
  const [savingOdeme, setSavingOdeme] = useState(false);
  const [loadingCariPdf, setLoadingCariPdf] = useState(false);
  const [odemeIdempotencyKey, setOdemeIdempotencyKey] = useState(null);
  const [odemeForm, setOdemeForm] = useState({
    tutar: "",
    tarih: new Date().toISOString().slice(0, 10),
    aciklama: "",
    cek_no: "",
  });

  const fetchCari = async () => {
    if (!firmaId) return;
    try {
      const resp = await axios.get(`${nodeURL}/getFirmaCari/${firmaId}`);
      setCari(resp.data);
    } catch (e) {
      toast.error("Cari getirilemedi!", { position: "top-right" });
    }
  };

  useEffect(() => {
    if (show && firmaId) {
      setShowCariDetails(false);
      setOdemeIdempotencyKey(generateIdempotencyKey());
      setCari(null);
      fetchCari();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, firmaId]);

  if (!show) return null;

  const planlananToplam = (cari?.gelecek_borclar || []).reduce(
    (acc, b) => acc + Number(b.tutar || 0),
    0
  );
  const vadesiGelmisBakiye = Number(cari?.bakiye || 0);
  const toplamBakiye = vadesiGelmisBakiye + planlananToplam;

  const handleSave = async () => {
    if (savingOdeme) return;
    if (!odemeForm.tutar || Number(odemeForm.tutar) <= 0) {
      toast.error("Lütfen geçerli bir tutar girin.", { position: "top-right" });
      return;
    }
    try {
      setSavingOdeme(true);
      toast.info("Ödeme kaydediliyor...", { position: "top-right", autoClose: 1200 });
      await axios.post(`${nodeURL}/firmaOdemeEkle`, {
        firmaId,
        tutar: odemeForm.tutar,
        tarih: odemeForm.tarih,
        aciklama: odemeForm.aciklama,
        cek_no: odemeForm.cek_no,
        idempotencyKey: odemeIdempotencyKey,
      });
      toast.success("Ödeme eklendi!", { position: "top-right" });
      setOdemeForm({
        tutar: "",
        tarih: new Date().toISOString().slice(0, 10),
        aciklama: "",
        cek_no: "",
      });
      // Başarılı kayıttan sonra yeni bir anahtar üret ki sonraki
      // (gerçekten farklı) ödeme ON CONFLICT ile sessizce yutulmasın.
      setOdemeIdempotencyKey(generateIdempotencyKey());
      fetchCari();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "Ödeme eklenemedi!", { position: "top-right" });
    } finally {
      setSavingOdeme(false);
    }
  };

  // document.body'ye portal ile render edilir: bu sayfaların dış kutusunda
  // backdropFilter (blur) kullanılıyor, bu da CSS'te position:fixed için yeni
  // bir konumlanma bağlamı açıyor — modal, sayfa scroll'undan bağımsız olarak
  // ekranın ortasında değil o kutunun en üstünde görünüyordu. Portal bu
  // sorunu kökten çözer.
  return createPortal(
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        overflowY: "auto",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 2000,
      }}
    >
      <div className="modal-dialog modal-lg modal-fullscreen-sm-down" style={{ margin: "1.75rem auto" }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title me-auto">
              Firma Cari / Ödeme{firmaAdi ? ` — ${firmaAdi}` : ""}
            </h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger me-3"
              disabled={loadingCariPdf || !cari}
              onClick={async () => {
                if (!cari) return;
                try {
                  setLoadingCariPdf(true);
                  await openCariPdf({ nodeURL, firmaAdi: firmaAdi || cari?.firma_adi || "Firma", cariData: cari });
                } catch (e) {
                  toast.error("Cari PDF indirilemedi!", { position: "top-right" });
                } finally {
                  setLoadingCariPdf(false);
                }
              }}
            >
              {loadingCariPdf ? "PDF..." : "PDF İndir"}
            </button>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <div><strong>Toplam Borç:</strong> {formatTutar(cari?.toplamBorc)}</div>
              {Number(cari?.toplamIptal || 0) > 0 && (
                <div className="text-muted">
                  <strong>İptal Edilen (İade):</strong> {formatTutar(cari.toplamIptal)}
                </div>
              )}
              <div><strong>Toplam Ödeme:</strong> {formatTutar(cari?.toplamOdeme)}</div>
              <div className="fs-6"><strong>Vadesi Gelmiş Bakiye:</strong> {formatTutar(vadesiGelmisBakiye)}</div>
              <div><strong>Planlanan Borç:</strong> {formatTutar(planlananToplam)}</div>
              <div className="fw-bold"><strong>Toplam Bakiye:</strong> {formatTutar(toplamBakiye)}</div>
              <button
                className="btn btn-sm btn-outline-secondary mt-2"
                type="button"
                onClick={() => setShowCariDetails((v) => !v)}
              >
                {showCariDetails ? "Detayı Gizle" : "Detayı Göster"}
              </button>
            </div>

            <div className="border rounded p-2 mb-3">
              <div className="fw-bold mb-2">Ödeme Ekle</div>
              <div className="row g-2">
                <div className="col-12 col-md-3">
                  <input
                    className="form-control"
                    type="number"
                    placeholder="Tutar"
                    value={odemeForm.tutar}
                    onChange={(e) => setOdemeForm({ ...odemeForm, tutar: e.target.value })}
                    disabled={savingOdeme}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <input
                    className="form-control"
                    type="date"
                    value={odemeForm.tarih}
                    onChange={(e) => setOdemeForm({ ...odemeForm, tarih: e.target.value })}
                    disabled={savingOdeme}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Çek no (opsiyonel)"
                    value={odemeForm.cek_no}
                    onChange={(e) => setOdemeForm({ ...odemeForm, cek_no: e.target.value })}
                    disabled={savingOdeme}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Açıklama"
                    value={odemeForm.aciklama}
                    onChange={(e) => setOdemeForm({ ...odemeForm, aciklama: e.target.value })}
                    disabled={savingOdeme}
                  />
                </div>
              </div>
              <div className="mt-2">
                <button className="btn btn-success" type="button" disabled={savingOdeme} onClick={handleSave}>
                  {savingOdeme ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>

            {showCariDetails && (
              <>
                <div className="fw-bold mb-2">Vadesi Gelmiş Borçlar</div>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Dönem</th>
                      <th>Ürün</th>
                      <th>Adet</th>
                      <th>Birim Fiyat</th>
                      <th>Açıklama</th>
                      <th>Tutar</th>
                      <th>Kalan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupBorcRows((cari?.borclar || []).filter((b) => Number(b.kalan_tutar || 0) > 0))
                      .slice(0, 50)
                      .map((g, i) => (
                        <tr key={i}>
                          <td>{formatTarih(g.donem)}</td>
                          <td>{g.urun_adi}</td>
                          <td>{g.adet}</td>
                          <td>{g.urun_birim_fiyat != null ? Number(g.urun_birim_fiyat).toFixed(2) : "-"}</td>
                          <td>{g.aciklama}</td>
                          <td>{formatTutar(g.toplamTutar)}</td>
                          <td>{formatTutar(g.toplamKalan)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                <div className="fw-bold mb-2">Planlanan Borç</div>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Dönem</th>
                      <th>Ürün</th>
                      <th>Adet</th>
                      <th>Birim Fiyat</th>
                      <th>Açıklama</th>
                      <th>Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupBorcRows(cari?.gelecek_borclar || [])
                      .slice(0, 50)
                      .map((g, i) => (
                        <tr key={i}>
                          <td>{formatTarih(g.donem)}</td>
                          <td>{g.urun_adi}</td>
                          <td>{g.adet}</td>
                          <td>{g.urun_birim_fiyat != null ? Number(g.urun_birim_fiyat).toFixed(2) : "-"}</td>
                          <td>{g.aciklama}</td>
                          <td>{formatTutar(g.toplamTutar)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                <div className="fw-bold mb-2">Ödemeler</div>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Tutar</th>
                      <th>Çek No</th>
                      <th>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cari?.hareketler || [])
                      .filter((h) => h.hareket_tipi === "ODEME")
                      .slice(-50)
                      .reverse()
                      .map((h) => (
                        <tr key={h.id}>
                          <td>{formatTarih(h.tarih)}</td>
                          <td>{formatTutar(Math.abs(Number(h.tutar || 0)))}</td>
                          <td>{h.cek_no || "-"}</td>
                          <td>{h.aciklama || "-"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Kapat</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FirmaCariModal;
